import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog.jsx';
import { Button } from '../ui/button.jsx';
import { ShieldCheck } from 'lucide-react';
import ApiCall from '../../../Apicall/ApiCall';
import BankIdAuthModal from '../../../pages/Landing/BankIdAuthModal';
import { dsToast } from '../../utils/dsToast';
import { useBankIdReturn } from '../../../hooks/useBankIdReturn';
import { findPending, claimResume } from '../../../utils/bankIdSession';

// Verification orders run under their own scope, so no login dialog ever resumes one as a login.
const LINK_SCOPE = 'link';

const resultOf = (res) => res?.data?.result ?? res?.result;

/** Verified = the server says so; an older server only sends the personal number. */
export const isVerifiedProfile = (profile) =>
  profile?.isBankIdVerified ?? /^\d{12}$/.test(String(profile?.bankIdSSN ?? '').replace(/\D/g, ''));

/**
 * BankID identity verification for a signed-in (typically e-mail registered) user, right where a
 * step needs it — starting or joining a deal. The step waits and then carries on:
 *
 *   const { ensureVerified, verificationDialogs } = useBankIdVerification();
 *   if (!(await ensureVerified())) return;   // false: the user cancelled; nothing was lost
 *   ...                                      // render {verificationDialogs} once
 *
 * ensureVerified({ explain: false }) opens BankID straight away (the user chose to verify, e.g.
 * on the profile page), without the "BankID is required to start a deal" explanation.
 *
 * Already verified accounts pass straight through (no repeat BankID). Verifying links the identity
 * to the current account (/BankId/Link); the server refuses an identity that is on another account.
 */
export function useBankIdVerification() {
  const { t } = useTranslation();
  const [view, setView] = useState(null);             // null | 'explain' | 'verify'
  const [resumeOrderRef, setResumeOrderRef] = useState(null);
  const waitingRef = useRef(null);
  const verifiedRef = useRef(false);

  const settle = useCallback((ok) => {
    const resolve = waitingRef.current;
    waitingRef.current = null;
    setView(null);
    setResumeOrderRef(null);
    resolve?.(ok);
  }, []);

  const ensureVerified = useCallback(async ({ explain = true } = {}) => {
    if (verifiedRef.current) return true;
    try {
      const session = resultOf(await ApiCall({ url: '/Session/GetCurrentLoginInformations', method: 'GET' }));
      const userId = session?.user?.id;
      if (userId) {
        const profile = resultOf(await ApiCall({ url: '/User/GetUserProfileById', method: 'GET', params: { Id: userId } }));
        if (isVerifiedProfile(profile)) {
          verifiedRef.current = true;
          return true;
        }
      }
    } catch (_) {
      // Could not tell: let the step go on — the server enforces the rule and answers 4031.
      return true;
    }
    return new Promise((resolve) => {
      waitingRef.current = resolve;
      setView(explain ? 'explain' : 'verify');
    });
  }, []);

  // The BankID app's return reloaded this page while a verification ran: finish that order.
  useBankIdReturn(() => {
    const pending = findPending({ purpose: 'auth', scope: LINK_SCOPE });
    if (pending && claimResume(pending.orderRef)) {
      setResumeOrderRef(pending.orderRef);
      setView('verify');
    }
  });

  const verificationDialogs = (
    <>
      <Dialog open={view === 'explain'} onOpenChange={(open) => { if (!open) settle(false); }}>
        <DialogContent overlayClassName="bg-black/50" closeLabel={t('verify.close')} className="ds-scope font-ds-sans sm:max-w-[440px] p-6 bg-ds-sheet border border-ds-rule-edge rounded-[13px]">
          <DialogHeader className="space-y-2 mb-4">
            <ShieldCheck className="mx-auto h-8 w-8 text-ds-green" aria-hidden="true" />
            <DialogTitle className="font-ds-serif text-[24px] font-bold text-ds-ink text-center">
              {t('verify.bankIdRequiredTitle')}
            </DialogTitle>
            <DialogDescription className="text-[15px] text-ds-text text-center">
              {t('verify.bankIdRequiredText')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button onClick={() => setView('verify')} className="w-full font-semibold h-11 rounded-[9px] bg-ds-ink text-[15px] hover:bg-ds-ink-hover">
              {t('verify.verifyWithBankId')}
            </Button>
            <Button onClick={() => settle(false)} variant="outline" className="w-full h-11 font-semibold rounded-[9px]">
              {t('verify.notNow')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <BankIdAuthModal
        open={view === 'verify'}
        purpose="link"
        scope={LINK_SCOPE}
        resumeOrderRef={resumeOrderRef}
        onClose={() => settle(false)}
        onSuccess={() => {
          verifiedRef.current = true;
          dsToast('success', { title: t('verify.verifiedTitle'), text: t('verify.verifiedText') }, { position: 'bottom-right' });
          settle(true);
        }}
      />
    </>
  );

  return { ensureVerified, verificationDialogs };
}
